import React from "react";
import { useI18n } from "../i18n";
import { useSettings } from "../contexts/SettingsContext";
import { ShieldAlert, PackageCheck, Undo2, Ban, Eye } from "lucide-react";

const MarketplacePolicy = () => {
  const { lang } = useI18n();
  const { settings } = useSettings();
  const isNL = lang === "nl";
  const summary = (isNL ? settings?.marketplace?.policy_summary_nl : settings?.marketplace?.policy_summary_en) || "";

  return (
    <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="marketplace-policy-page">
      <div className="font-label text-ink-muted">{isNL ? "MARKETPLACE" : "MARKETPLACE"}</div>
      <h1 className="font-display text-5xl tracking-tighter font-light">{isNL ? "Marketplace beleid" : "Marketplace policy"}</h1>
      {summary && <p className="mt-6 text-lg text-ink-muted leading-relaxed">{summary}</p>}

      <div className="mt-12 space-y-6">
        <Block icon={Ban} title={isNL ? "Geen dropshipping" : "No dropshipping"}>
          {isNL
            ? "Producten moeten op voorraad zijn of binnen 1 werkdag verzendbaar. Wij staan geen pure dropshipping toe vanuit AliExpress, Temu, of soortgelijke marktplaatsen. SnapShop kan op elk moment voorraadbewijs opvragen."
            : "Products must be in stock or shippable within 1 business day. We do not allow pure dropshipping from AliExpress, Temu, or similar marketplaces. SnapShop may request stock proof at any time."}
        </Block>
        <Block icon={PackageCheck} title={isNL ? "Voorraad & verzending" : "Stock & shipping"}>
          {isNL
            ? "Verkopers verzenden binnen 1 werkdag na betaling via PostNL of DHL met track & trace. SnapShop hanteert één uniform verzendtarief — verkopers onderhandelen niet apart met klanten over verzendkosten."
            : "Sellers ship within 1 business day of payment via PostNL or DHL with track & trace. SnapShop applies a single unified shipping rate — sellers do not negotiate separately with customers on shipping costs."}
        </Block>
        <Block icon={Undo2} title={isNL ? "Retourbeleid (30 dagen)" : "Returns policy (30 days)"}>
          {isNL
            ? "Eén retourbeleid geldt voor alle verkopers: klanten kunnen producten binnen 30 dagen retourneren in originele staat. SnapShop handelt de retour af en verrekent de kosten met de eerstvolgende uitbetaling."
            : "A single returns policy applies to all sellers: customers may return products within 30 days in original condition. SnapShop handles the return process and deducts costs from the next payout."}
        </Block>
        <Block icon={Eye} title={isNL ? "Toegestane producten" : "Allowed products"}>
          {isNL ? (
            <>
              <p className="mb-2">Toegestane categorieën: home & living, kleine elektronica/gadgets, huisdier, keuken, auto-accessoires, mode (geselecteerd).</p>
              <p>Verboden: namaakproducten, gevaarlijke goederen, recept-medicijnen, wapens, alcohol/tabak, volwassen-producten, levende dieren.</p>
            </>
          ) : (
            <>
              <p className="mb-2">Allowed categories: home & living, small electronics/gadgets, pet, kitchen, car accessories, selected fashion.</p>
              <p>Prohibited: counterfeit goods, hazardous materials, prescription medicines, weapons, alcohol/tobacco, adult products, live animals.</p>
            </>
          )}
        </Block>
        <Block icon={ShieldAlert} title={isNL ? "Handhaving" : "Enforcement"}>
          {isNL ? (
            <ul className="list-disc pl-5 space-y-1">
              <li>SnapShop kan producten direct verbergen of verwijderen die het beleid schenden.</li>
              <li>Verkopers ontvangen een waarschuwing per e-mail bij eerste overtreding.</li>
              <li>Bij herhaalde of ernstige overtredingen wordt het verkopersaccount onmiddellijk geschorst.</li>
              <li>Beslissingen zijn definitief — bezwaar mogelijk via support@snapshop.it.com.</li>
            </ul>
          ) : (
            <ul className="list-disc pl-5 space-y-1">
              <li>SnapShop may immediately hide or remove products that violate the policy.</li>
              <li>Sellers receive an email warning on the first violation.</li>
              <li>Repeated or serious violations result in immediate suspension.</li>
              <li>Decisions are final — appeals via support@snapshop.it.com.</li>
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
};

const Block = ({ icon: Icon, title, children }) => (
  <div className="border border-black/10 bg-white p-6 flex gap-4">
    <div className="w-10 h-10 bg-secondary-warm flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-terracotta" />
    </div>
    <div className="flex-1">
      <div className="font-display text-lg mb-1.5">{title}</div>
      <div className="text-sm text-ink-muted leading-relaxed">{children}</div>
    </div>
  </div>
);

export default MarketplacePolicy;
